import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FJCloudTranscodingModal from './FJCloudTranscodingModal';
import FJActionModal from '../../../redux/action/FJActionModal';
import FJActionCloudTranscoding from '../../../redux/action/FJActionCloudTranscoding';
import FJModal from '../FJModal';
import FJConstant from '../../../constants/FJConstant';
import { CLOUD_TRANSCODE_SUPPORTED_FORMATS } from '../../../config/dataConfig/FJCloudTranscodingData';

/**
 * 云转码进度弹窗容器组件
 * 连接Redux状态和组件，处理各种action调用
 */
const FJCloudTranscodingModalContainer = () => {
    const dispatch = useDispatch();
    const timer = useRef(null);

    // 从 Redux store 获取状态
    const cloudTranscodingModalData = useSelector(state => state.modals.cloudTranscodingModalData || {});
    const transcodingStatus = useSelector(state => state.cloudTranscoding.transcodingStatus || '');
    const error = useSelector(state => state.cloudTranscoding.error || '');
    const transcodingTasks = useSelector(state => state.cloudTranscoding.transcodingTasks || []);
    const config = useSelector(state => state.cloudTranscoding.config || {});
    const { display, onClose, onConfirm, onCancel, onReupload, onRestart, inputOptions } = cloudTranscodingModalData;

    // 关闭模态框
    const handleClose = useCallback(() => {
        onClose?.();
        dispatch(FJActionModal.hideCloudTranscodingModalAction());
    }, [dispatch, onClose]);

    // 取消转码任务
    const handleCancelTask = useCallback(
        async taskIds => {
            try {
                dispatch(FJActionCloudTranscoding.cancelTranscodingTaskAction(taskIds));
                dispatch(FJActionModal.hideCloudTranscodingModalAction());
                onCancel?.();
            } catch (error) {
                console.error('failed to cancel transcoding task', error);
            }
        },
        [dispatch, onCancel],
    );

    // 重新开始转码
    const handleRestart = useCallback(() => {
        dispatch(FJActionCloudTranscoding.retryFailedTasksAction());
        onRestart?.();
    }, [dispatch, onRestart]);

    // 确认转码结果
    const handleConfirm = useCallback(async () => {
        const successTasks = transcodingTasks.filter(task => task.status === FJConstant.SUCCESS);
        onConfirm?.(successTasks.map(task => task.file));
        dispatch(FJActionModal.hideCloudTranscodingModalAction());
    }, [onConfirm, dispatch, transcodingTasks]);

    // 重新上传
    const handleReupload = useCallback(
        files => {
            if (onReupload && files && files.length > 0) {
                // 将FileList转换为Array
                const fileArray = Array.from(files);
                dispatch(FJActionModal.hideCloudTranscodingModalAction());
                onReupload(fileArray);
            }
        },
        [dispatch, onReupload],
    );

    // 监听转码状态
    useEffect(() => {
        if (display && transcodingStatus === FJConstant.SUCCESS) {
            timer.current = setTimeout(() => {
                onClose?.();
                dispatch(FJActionModal.hideCloudTranscodingModalAction());
            }, 2000);
        } else {
            clearTimeout(timer.current);
        }
        return () => {
            clearTimeout(timer.current);
        };
    }, [dispatch, display, onClose, transcodingStatus]);

    // 包装inputOptions，添加accept
    const wrappedInputOptions = useMemo(() => {
        let accept = '';
        accept = '.' + CLOUD_TRANSCODE_SUPPORTED_FORMATS[config?.mediaType]?.input.join(',.');
        return {
            ...inputOptions,
            accept,
        };
    }, [config?.mediaType, inputOptions]);

    return (
        <FJModal visible={display}>
            <FJCloudTranscodingModal
                active={display}
                onClose={handleClose}
                onCancelTask={handleCancelTask}
                onRestart={handleRestart}
                transcodingStatus={transcodingStatus}
                error={error}
                transcodingTasks={transcodingTasks}
                onConfirm={handleConfirm}
                onReupload={handleReupload}
                inputOptions={wrappedInputOptions}
            />
        </FJModal>
    );
};

export default FJCloudTranscodingModalContainer;
